import { z } from 'zod';
import { addDaysToLocalDate, localDateOf } from './timezone.js';
/**
 * Evolução do treino (P2 do personal, estudo: "histórico de carga, recordes, 1RM estimado, volume
 * por grupo muscular, frequência"). Conta feita só com o que o aluno registrou: a série mais
 * forte de cada exercício por treino, o 1RM estimado pela fórmula de Epley (só até 12
 * repetições, onde ela é confiável), as séries feitas por músculo e os treinos por semana.
 */
export const progressPointSchema = z.object({
    day: z.string(),
    kg: z.number().nullable(),
    reps: z.number().nullable(),
    /** 1RM estimado (Epley); nulo sem carga ou com mais de 12 repetições. */
    e1rm: z.number().nullable(),
});
export const exerciseProgressSchema = z.object({
    key: z.string(),
    name: z.string(),
    primary: z.array(z.string()),
    points: z.array(progressPointSchema),
    best: progressPointSchema.nullable(),
    /** O recorde caiu nos últimos 14 dias. */
    recentRecord: z.boolean(),
});
export const workoutProgressSchema = z.object({
    /** Treinos por semana, da mais antiga para a atual (segunda a domingo). */
    weeks: z.array(z.object({ start: z.string(), sessions: z.number() })),
    perWeek: z.number().nullable(),
    /** Séries feitas por músculo nas últimas 4 semanas. */
    muscles: z.array(z.object({ muscle: z.string(), sets: z.number() })),
    exercises: z.array(exerciseProgressSchema),
    totalSessions: z.number(),
});
/** 1RM estimado (Epley): carga × (1 + repetições/30), só de 1 a 12 repetições. */
export function estimatedOneRm(kg, reps) {
    if (kg === null || reps === null || kg <= 0 || reps < 1 || reps > 12)
        return null;
    return reps === 1 ? kg : Math.round(kg * (1 + reps / 30) * 10) / 10;
}
const stronger = (a, b) => (a.e1rm ?? -1) - (b.e1rm ?? -1) || (a.kg ?? -1) - (b.kg ?? -1) || (a.reps ?? -1) - (b.reps ?? -1);
/** Segunda-feira da semana do dia. */
const weekStart = (day) => {
    const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
    return addDaysToLocalDate(day, -((weekday + 6) % 7));
};
export function workoutProgress(input) {
    const span = input.weeks ?? 8;
    const items = new Map();
    // A ordem da ficha mais nova (as fichas vêm da mais nova para a mais velha) desempata a lista.
    const order = new Map();
    for (const plan of input.plans)
        for (const sheet of plan.content.sheets)
            for (const item of sheet.items) {
                const key = item.exerciseId ? `ex:${item.exerciseId}` : `it:${item.id}`;
                items.set(item.id, { key, name: item.name, primary: item.primary });
                if (!order.has(key))
                    order.set(key, order.size);
            }
    const thisWeek = weekStart(input.today);
    const starts = Array.from({ length: span }, (_, index) => addDaysToLocalDate(thisWeek, -7 * (span - 1 - index)));
    const perWeekCount = new Map(starts.map((start) => [start, 0]));
    const fourWeeks = addDaysToLocalDate(thisWeek, -21);
    const muscles = new Map();
    const byExercise = new Map();
    for (const session of input.sessions) {
        const day = localDateOf(new Date(session.finishedAt), input.timeZone);
        const week = weekStart(day);
        if (perWeekCount.has(week))
            perWeekCount.set(week, perWeekCount.get(week) + 1);
        for (const set of session.sets) {
            const item = items.get(set.itemId);
            if (!item)
                continue;
            if (day >= fourWeeks)
                for (const muscle of item.primary)
                    muscles.set(muscle, (muscles.get(muscle) ?? 0) + 1);
            const point = {
                day,
                kg: set.kg,
                reps: set.reps,
                e1rm: estimatedOneRm(set.kg, set.reps),
            };
            const entry = byExercise.get(item.key) ?? {
                name: item.name,
                primary: [...item.primary],
                points: new Map(),
            };
            const current = entry.points.get(day);
            if (!current || stronger(point, current) > 0)
                entry.points.set(day, point);
            byExercise.set(item.key, entry);
        }
    }
    const recent = addDaysToLocalDate(input.today, -14);
    const exercises = [...byExercise.entries()]
        .map(([key, entry]) => {
        const points = [...entry.points.values()].sort((a, b) => a.day.localeCompare(b.day));
        const best = points.reduce((top, point) => (!top || stronger(point, top) > 0 ? point : top), null);
        // Recorde recente: o melhor é deste período e superou o que havia antes dele.
        const before = points.filter((point) => point.day < recent);
        const beforeBest = before.reduce((top, point) => (!top || stronger(point, top) > 0 ? point : top), null);
        const recentRecord = Boolean(best && best.day >= recent && beforeBest && stronger(best, beforeBest) > 0);
        return { key, name: entry.name, primary: entry.primary, points, best, recentRecord };
    })
        .sort((a, b) => b.points.length - a.points.length || (order.get(a.key) ?? 999) - (order.get(b.key) ?? 999));
    return {
        weeks: starts.map((start) => ({ start, sessions: perWeekCount.get(start) ?? 0 })),
        perWeek: input.perWeek,
        muscles: [...muscles.entries()]
            .map(([muscle, sets]) => ({ muscle, sets }))
            .sort((a, b) => b.sets - a.sets),
        exercises,
        totalSessions: input.sessions.length,
    };
}
